// Models
import {Signal} from "../records/models"
import {Beat, ENVELOPE_SIDE, PointKey, POINT_KEYS} from "./model"

/**
 * Does each mark actually contain every lead?
 *
 * The six boundaries are an ENVELOPE: a start is the earliest onset of any lead,
 * an end the latest offset of any lead. A technician reading one lead — which is
 * what the eye does unless it is stopped — places the mark inside the wave of
 * some other lead, and nothing about the screen says so. Over a few thousand
 * beats that is a quiet, systematic bias in the finished dataset.
 *
 * This checks the property directly against the signal, per beat, per boundary:
 * is any lead still deflected on the OUTSIDE of this mark? If one is, the mark
 * does not contain it and the check says which lead and by how many
 * milliseconds.
 *
 * WHAT THIS IS NOT. It does not find waves and it does not propose boundaries —
 * that is the analysis service's job and duplicating it here would quietly
 * create a second, worse detector. It only answers a question about a mark a
 * human has already made, which is the one thing a human cannot check by eye
 * across twelve leads at once.
 */

export type BoundaryCheck = {
    ok: boolean
    /** How far the mark must move to contain every lead, in ms. 0 when ok. */
    outByMs: number
    /**
     * True when the scan reached its limit still deflected, so `outByMs` is a
     * floor rather than the distance. Reporting "90 ms" for a mark that is
     * really 200 ms inside its wave would understate how wrong it is, and the
     * technician would trust the number.
     */
    atLeast: boolean
    /** The lead that falls outside, or "" when ok. */
    lead: string
}

export type BeatCheck = {[key in PointKey]: BoundaryCheck | null};

/**
 * How far outside a mark to look, in milliseconds.
 *
 * Enough to cover a lead that starts well before the marked one, short enough
 * that the scan cannot wander into the neighbouring wave and call that a
 * violation.
 */
const LOOK_MS = 90;

/**
 * Multiples of the noise floor that count as the trace being deflected.
 *
 * Three is the usual "clearly above the grass" threshold. Lower starts calling
 * baseline wander a P wave; higher misses the shallow start of a real one.
 */
const ACTIVE_K = 3;

/** Per-lead baseline level and noise floor, in raw sample units. */
export type LeadFloor = {lead: string, baseline: number, noise: number};

function median(values: number[]): number {
    if (!values.length) {
        return 0
    }

    const sorted = values.slice().sort((a, b) => a - b);

    return sorted[Math.floor(sorted.length / 2)]
}

/**
 * Estimate each lead's baseline and noise from the quietest stretches.
 *
 * Taken between beats — after one T has finished and before the next P starts —
 * because that is the only part of the record where the trace is supposed to be
 * doing nothing, so whatever is there is the noise this check has to see past.
 *
 * @param signals
 * @param beats
 * @param sampleRate
 */
export function leadFloors(signals: Signal[], beats: Beat[], sampleRate: number): LeadFloor[] {
    const windows: {from: number, to: number}[] = [];

    for (let i = 0; i < beats.length - 1; i += 1) {
        const from = beats[i].points.tEnd;
        const to = beats[i + 1].points.pStart;

        if (from !== null && to !== null && to - from > 40) {
            /* stay a little inside, so a slightly late tEnd does not poison it */
            windows.push({from: from + 10, to: to - 10})
        }
    }

    return signals.map(signal => {
        const samples: number[] = [];

        windows.forEach(w => {
            const a = Math.max(0, Math.round((w.from * sampleRate) / 1000));
            const b = Math.min(signal.data.length, Math.round((w.to * sampleRate) / 1000));

            for (let i = a; i < b; i += 1) {
                samples.push(signal.data[i])
            }
        });

        if (samples.length < 20) {
            /* no usable gap — fall back to the whole record's spread, which is
               pessimistic but never pretends the trace is cleaner than it is */
            for (let i = 0; i < signal.data.length; i += 7) {
                samples.push(signal.data[i])
            }
        }

        const baseline = median(samples);
        const deviations = samples.map(v => Math.abs(v - baseline));

        return {
            lead: signal.lead,
            baseline: baseline,
            /* median absolute deviation: one loud artefact cannot inflate it the
               way a standard deviation would */
            noise: Math.max(1, median(deviations)),
        }
    })
}

function valueAt(signal: Signal, ms: number, sampleRate: number): number | null {
    const i = Math.round((ms * sampleRate) / 1000);

    return i >= 0 && i < signal.data.length ? signal.data[i] : null
}

/**
 * Check one boundary of one beat.
 *
 * A start must have nothing to its left; an end nothing to its right. So for
 * each lead: if the trace is deflected AT the mark, walk outward to where it
 * settles — that is where this lead's wave really begins or ends, and the
 * distance is how far the mark fails to contain it. The worst lead wins,
 * because the envelope has to contain all of them.
 *
 * @param signals
 * @param floors
 * @param markMs
 * @param side "earliest" for a start, "latest" for an end
 * @param sampleRate
 */
export function checkBoundary(
    signals: Signal[],
    floors: LeadFloor[],
    anchorMs: number,
    markMs: number,
    side: "earliest" | "latest",
    limitMs: number,
    sampleRate: number,
): BoundaryCheck {
    const stepMs = 1000 / sampleRate;
    const outward = side === "earliest" ? -1 : 1;

    let worst = 0;
    let worstLead = "";
    let worstClamped = false;

    signals.forEach((signal, index) => {
        const floor = floors[index];

        if (!floor) {
            return
        }

        const threshold = floor.noise * ACTIVE_K;

        /*
         * Walk out from the ANCHOR, which is inside the wave, to where this
         * lead settles. That is the lead's own boundary.
         *
         * The previous version instead asked whether the trace was deflected AT
         * the mark, which cannot tell "still inside the QRS" from "now inside
         * the T". Following its advice walked QRS end rightwards into the T
         * wave, where it stayed deflected and the warning never cleared — 120
         * presses and still complaining. Anchoring fixes that: once the mark is
         * past where the lead settles, it contains it, whatever else is
         * happening at the mark.
         */
        let edge = anchorMs;

        for (let d = stepMs; d <= LOOK_MS * 3; d += stepMs) {
            const at = anchorMs + outward * d;

            if ((outward < 0 && at < limitMs) || (outward > 0 && at > limitMs)) {
                break
            }

            const v = valueAt(signal, at, sampleRate);

            if (v === null) {
                break
            }

            if (Math.abs(v - floor.baseline) <= threshold) {
                break
            }

            edge = at
        }

        /* a start must be at or before every lead's edge, an end at or after */
        const miss = outward < 0 ? markMs - edge : edge - markMs;

        if (miss > stepMs && miss > worst) {
            worst = miss;
            worstLead = signal.lead;
            /* the scan ran into the neighbouring wave rather than settling, so
               the distance is a floor */
            worstClamped = Math.abs(edge - anchorMs) >= LOOK_MS * 3 - stepMs
        }
    });

    return {
        ok: worst === 0,
        outByMs: Math.round(worst),
        atLeast: worstClamped,
        lead: worstLead,
    }
}

/**
 * Check all six boundaries of one beat.
 *
 * An absent mark is not a failure — a beat with no P has nothing to contain.
 *
 * @param signals
 * @param floors
 * @param beat
 * @param sampleRate
 */
export function checkBeat(
    signals: Signal[],
    floors: LeadFloor[],
    beat: Beat,
    sampleRate: number,
): BeatCheck {
    const out = {} as BeatCheck;
    const p = beat.points;

    /*
     * Each boundary needs a point known to be INSIDE its wave to walk out from,
     * and a point past which the scan must not wander into the neighbour.
     *
     * The QRS anchors on the R peak, which is supplied and never in doubt. P
     * and T anchor on the midpoint of their own pair — imperfect if both marks
     * are badly placed, but the marks are the service's estimate, not noise, and
     * the limits stop a bad one from reaching the next wave.
     */
    const mid = (a: number | null, b: number | null, fallback: number) =>
        (a !== null && b !== null ? (a + b) / 2 : fallback);

    const pMid = mid(p.pStart, p.pEnd, beat.rPeakMs - 120);
    const tMid = mid(p.tStart, p.tEnd, beat.rPeakMs + 240);

    const anchor: {[key in PointKey]: number} = {
        pStart: pMid, pEnd: pMid,
        qrsStart: beat.rPeakMs, qrsEnd: beat.rPeakMs,
        tStart: tMid, tEnd: tMid,
    };

    const limit: {[key in PointKey]: number} = {
        pStart: beat.rPeakMs - 500,
        pEnd: p.qrsStart === null ? beat.rPeakMs : p.qrsStart,
        qrsStart: p.pEnd === null ? beat.rPeakMs - 200 : p.pEnd,
        qrsEnd: p.tStart === null ? beat.rPeakMs + 200 : p.tStart,
        tStart: p.qrsEnd === null ? beat.rPeakMs : p.qrsEnd,
        tEnd: beat.rPeakMs + 700,
    };

    POINT_KEYS.forEach(key => {
        const ms = p[key];

        out[key] = ms === null
            ? null
            : checkBoundary(
                signals,
                floors,
                anchor[key],
                ms,
                ENVELOPE_SIDE[key],
                limit[key],
                sampleRate,
            )
    });

    return out
}

/** How many of a beat's marks fail to contain every lead. */
export function failureCount(check: BeatCheck): number {
    return POINT_KEYS.filter(key => {
        const c = check[key];

        return c !== null && !c.ok
    }).length
}
